import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MerchantCredential } from '../../api/clientCredentials';

export interface ClientCredentialsState {
    credentials: MerchantCredential[]
    environment: 'sandbox' | 'production';
    searchQuery: string;
    cursor: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    activeTab: number;
    loading: boolean;
    error: string | null;
    correlationId: string | null;
}

export const initialClientCredentialsState: ClientCredentialsState = {
    credentials: [],
    environment: 'sandbox',
    searchQuery: '',
    cursor: 0,
    pageSize: 10,
    hasNextPage: false,
    hasPrevPage: false,
    activeTab: 0,
    loading: false,
    error: null,
    correlationId: null,
};

const slice = createSlice({
    name: 'ClientCredentials',
    initialState: initialClientCredentialsState,
    reducers: {
        // Set loading state
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },

        // Set error
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            state.loading = false;
        },

        // Set correlation ID
        setCorrelationId: (state, action: PayloadAction<string | null>) => {
            state.correlationId = action.payload;
        },

        // Set credentials data
        setCredentials: (state, action: PayloadAction<{ 
            credentials: MerchantCredential[]; 
            hasNextPage: boolean;
            hasPrevPage: boolean;
        }>) => {
            const { credentials, hasNextPage, hasPrevPage } = action.payload;
            state.credentials = credentials;
            state.hasNextPage = hasNextPage;
            state.hasPrevPage = hasPrevPage;
            state.loading = false;
            state.error = null;
        },

        // Add a single credential
        addCredential: (state, action: PayloadAction<MerchantCredential>) => {
            const credential = action.payload;
            state.credentials.push(credential);
        },

        // Remove a credential
        removeCredential: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            //delete state.credentials.byId[id];
            //state.credentials.ids = state.credentials.ids.filter(credId => credId !== id);
            //state.totalItems -= 1;
        },

        // Toggle environment
        setEnvironment: (state, action: PayloadAction<'sandbox' | 'production'>) => {
            state.environment = action.payload;
            state.cursor = 0;
            state.searchQuery = '';
            state.credentials = [];
            state.hasNextPage = false;
            state.hasPrevPage = false;
            state.error = null;
            state.loading = true;
        },

        // Set search query
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
            state.cursor = 0; // Reset cursor when searching
        },

        // Set cursor for pagination
        setCursor: (state, action: PayloadAction<number>) => {
            state.cursor = action.payload;
        },

        // Set page size
        setPageSize: (state, action: PayloadAction<number>) => {
            state.pageSize = action.payload;
            state.cursor = 0;
        },

        // Set active tab
        setActiveTab: (state, action: PayloadAction<number>) => {
            state.activeTab = action.payload;
        },

        // Reset state
        resetState: () => initialClientCredentialsState,
    },
});

export const {
    setLoading,
    setError,
    setCorrelationId,
    setCredentials,
    addCredential,
    removeCredential,
    setEnvironment,
    setSearchQuery,
    setCursor,
    setPageSize,
    setActiveTab,
    resetState,
} = slice.actions;

export default slice.reducer;
